import React, { useCallback, useEffect, useRef, useState } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import { CircularProgress } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import Button from 'components/shared/Button'
import { ChoiceGroup, Radio } from 'components/ds/Choice'
import { Toast } from 'components/shared/Toaster'
import { getMySignature, resetMySignature, saveMySignature } from 'actions/user'
import strings from 'strings'
import SignaturePreview from './SignaturePreview'
import styles from './SignatureSettings.module.scss'

const S = strings.PROFILE_SIGNATURE

// The pad records at least 2x its on-screen size so the drawing stays sharp when printed.
const MIN_PIXEL_RATIO = 2
const TRIM_PADDING = 8

/** Crops the transparent margin around the strokes so the drawing scales well on the PDF. */
const trimmedDataUrl = (canvas) => {
  const { width, height } = canvas
  const pixels = canvas.getContext('2d').getImageData(0, 0, width, height).data
  let top = height, left = width, right = -1, bottom = -1
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pixels[(y * width + x) * 4 + 3] !== 0) {
        if (x < left) left = x
        if (x > right) right = x
        if (y < top) top = y
        if (y > bottom) bottom = y
      }
    }
  }
  if (right < 0) return null
  left = Math.max(0, left - TRIM_PADDING)
  top = Math.max(0, top - TRIM_PADDING)
  right = Math.min(width - 1, right + TRIM_PADDING)
  bottom = Math.min(height - 1, bottom + TRIM_PADDING)
  const out = document.createElement('canvas')
  out.width = right - left + 1
  out.height = bottom - top + 1
  out.getContext('2d').drawImage(canvas, left, top, out.width, out.height, 0, 0, out.width, out.height)
  return out.toDataURL('image/png')
}

const Option = ({ name, value, checked, onChange, title, help }) => (
  <Radio
    name={name}
    value={value}
    checked={checked}
    onChange={() => onChange(value)}
    className={styles.option}
    label={<span className={styles.optionText}><strong>{title}</strong><span>{help}</span></span>}
  />
)

const SignatureSettings = () => {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(null)
  const [mark, setMark] = useState('TYPED')
  const [stamp, setStamp] = useState('SEAL')
  const [drawing, setDrawing] = useState(false)
  const [padImage, setPadImage] = useState(null)
  const padRef = useRef(null)
  const padWidth = useRef(0)

  const apply = (data) => {
    setSaved(data)
    setMark(data.mark || 'TYPED')
    setStamp(data.stamp || 'SEAL')
    setDrawing(data.mark === 'DRAWN' && !data.drawnImage)
    setPadImage(null)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      apply(await getMySignature())
    } catch (e) {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Sizes the pad's pixel buffer to its on-screen box; the drawing is cleared when the box changes size.
  const sizePad = useCallback(() => {
    const pad = padRef.current
    if (!pad) return
    const canvas = pad.getCanvas()
    if (!canvas.offsetWidth || canvas.offsetWidth === padWidth.current) return
    padWidth.current = canvas.offsetWidth
    const ratio = Math.max(window.devicePixelRatio || 1, MIN_PIXEL_RATIO)
    canvas.width = canvas.offsetWidth * ratio
    canvas.height = canvas.offsetHeight * ratio
    canvas.getContext('2d').scale(ratio, ratio)
    pad.clear()
    setPadImage(null)
  }, [])

  useEffect(() => {
    if (!drawing || mark !== 'DRAWN') return undefined
    padWidth.current = 0
    sizePad()
    window.addEventListener('resize', sizePad)
    return () => window.removeEventListener('resize', sizePad)
  }, [drawing, mark, sizePad])

  const chooseMark = (value) => {
    setMark(value)
    if (value === 'DRAWN' && !saved?.drawnImage) setDrawing(true)
  }

  const clearPad = () => {
    if (padRef.current) padRef.current.clear()
    setPadImage(null)
  }

  const handleSave = async () => {
    const newDrawing = mark === 'DRAWN' && drawing ? padImage : null
    if (mark === 'DRAWN' && !newDrawing && !saved?.drawnImage) {
      Toast({ type: 'error', message: S.DRAW_REQUIRED })
      return
    }
    setSaving(true)
    try {
      apply(await saveMySignature({ mark, stamp, drawnImage: newDrawing }))
      Toast({ type: 'success', message: S.SAVED_TOAST })
    } catch (e) {
      // request() already shows the error
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    setSaving(true)
    try {
      apply(await resetMySignature())
      Toast({ type: 'success', message: S.RESET_TOAST })
    } catch (e) {
      // request() already shows the error
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className={styles.loading}><CircularProgress size={24} color="inherit" /></div>
  }
  if (loadError) {
    return (
      <Alert severity="error" action={<Button size="small" variant="text" onClick={load}>{S.RETRY}</Button>}>
        {S.LOAD_ERROR}
      </Alert>
    )
  }

  const previewImage = mark === 'DRAWN' ? (drawing ? padImage : saved?.drawnImage) : null

  return (
    <div className={styles.layout}>
      <div className={styles.form}>
        <h6 className={styles.title}>{S.TITLE}</h6>
        <p className={styles.description}>{S.DESCRIPTION}</p>

        <ChoiceGroup legend={S.MARK} className={styles.group}>
          <Option name="signature-mark" value="TYPED" checked={mark === 'TYPED'} onChange={chooseMark}
                  title={S.MARK_TYPED} help={S.MARK_TYPED_HELP} />
          <Option name="signature-mark" value="DRAWN" checked={mark === 'DRAWN'} onChange={chooseMark}
                  title={S.MARK_DRAWN} help={S.MARK_DRAWN_HELP} />
        </ChoiceGroup>

        {mark === 'DRAWN' && (
          drawing ? (
            <div className={styles.padBlock}>
              <div className={styles.pad}>
                <SignatureCanvas
                  ref={padRef}
                  penColor="#141828"
                  minWidth={1}
                  maxWidth={2.6}
                  clearOnResize={false}
                  canvasProps={{ className: styles.canvas, 'aria-label': S.DRAW_HINT }}
                  onEnd={() => setPadImage(trimmedDataUrl(padRef.current.getCanvas()))}
                />
              </div>
              <div className={styles.padActions}>
                <span className={styles.hint}>{S.DRAW_HINT}</span>
                <Button size="small" variant="text" onClick={clearPad} disabled={!padImage}>{S.CLEAR}</Button>
              </div>
            </div>
          ) : (
            <div className={styles.padActions}>
              <Button size="small" variant="outlined" onClick={() => setDrawing(true)}>{S.DRAW_AGAIN}</Button>
            </div>
          )
        )}

        <ChoiceGroup legend={S.STAMP} className={styles.group}>
          <Option name="signature-stamp" value="PLAIN" checked={stamp === 'PLAIN'} onChange={setStamp}
                  title={S.STAMP_PLAIN} help={S.STAMP_PLAIN_HELP} />
          <Option name="signature-stamp" value="STAMP" checked={stamp === 'STAMP'} onChange={setStamp}
                  title={S.STAMP_STAMP} help={S.STAMP_STAMP_HELP} />
          <Option name="signature-stamp" value="SEAL" checked={stamp === 'SEAL'} onChange={setStamp}
                  title={S.STAMP_SEAL} help={S.STAMP_SEAL_HELP} />
        </ChoiceGroup>

        <div className={styles.actions}>
          {saved?.configured && (
            <Button variant="text" onClick={handleReset} disabled={saving}>{S.RESET}</Button>
          )}
          <Button onClick={handleSave} loading={saving}>{S.SAVE}</Button>
        </div>
        {!saved?.configured && <p className={styles.hint}>{S.DEFAULT_NOTE}</p>}
      </div>

      <div className={styles.previewColumn}>
        <div className={styles.previewLabel}>{S.PREVIEW}</div>
        <SignaturePreview
          name={saved?.signerName}
          email={saved?.signerEmail}
          mark={previewImage ? 'DRAWN' : 'TYPED'}
          stamp={stamp}
          drawnImage={previewImage}
        />
      </div>
    </div>
  )
}

export default SignatureSettings
